import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, forkJoin, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { environment } from '@root/src/environments/environment';
import { AuthService } from './auth.service';

export interface RegionDictionaryNode {
  id: string;
  name: string;
  parentId?: string | null;
  level?: number;
  regionMapId?: string | null;
  children?: RegionDictionaryNode[];
  names?: Record<string, string>;
  expanded?: boolean;
}

interface RegionQueryResponse {
  items?: any[];
  data?: any[] | { items?: any[] };
}

@Injectable({
  providedIn: 'root'
})
export class RegionDictionaryService {
  private readonly regionUrl = `${environment.api}/dictionaries/Region/GetByQuery`;
  private readonly regionMapUrl = `${environment.api}/dictionaries/RegionMap/GetByQuery`;

  private regionMapsCache = new Map<string, any>();
  private allRegionMapsLoaded = false;

  constructor(
    private readonly http: HttpClient,
    private readonly authService: AuthService
  ) {}

  getRegionTree(): Observable<RegionDictionaryNode[]> {
    return forkJoin({
      level2: this.getRegionsByLevel(2),
      level3: this.getRegionsByLevel(3)
    }).pipe(
      map(({ level2, level3 }) => {
        const tree = level2.map(region => ({
          ...region,
          children: level3
            .filter(child => child.parentId === region.id)
            .map(child => ({ ...child }))
        }));
        return tree;
      })
    );
  }

  getRegionMapById(id: string): Observable<any | null> {
    if (!id) {
      return of(null);
    }

    if (this.regionMapsCache.has(id)) {
      return of(this.regionMapsCache.get(id));
    }

    return this.postQuery(this.regionMapUrl, {
      page: 1,
      size: 1,
      filters: [
        {
          filterField: 'id',
          filterValue: id,
          filterOperator: 0
        }
      ]
    }).pipe(
      map(response => {
        const items = this.extractItems(response);
        if (items.length) {
          this.regionMapsCache.set(id, items[0]);
          return items[0];
        }
        return null;
      }),
      catchError(() => of(null))
    );
  }

  getAllRegionMaps(): Observable<Record<string, any>> {
    if (this.allRegionMapsLoaded && this.regionMapsCache.size) {
      const existing: Record<string, any> = {};
      this.regionMapsCache.forEach((value, key) => {
        existing[key] = value;
      });
      return of(existing);
    }

    return this.postQuery(this.regionMapUrl, {
      page: 1,
      size: 10000
    }).pipe(
      map(response => {
        const items = this.extractItems(response);
        const result: Record<string, any> = {};
        items.forEach(item => {
          if (item?.id) {
            this.regionMapsCache.set(item.id, item);
            result[item.id] = item;
          }
        });
        this.allRegionMapsLoaded = true;
        return result;
      }),
      catchError(() => of({}))
    );
  }

  getRegionsByLevel(level: number): Observable<RegionDictionaryNode[]> {
    return this.postQuery(this.regionUrl, {
      page: 1,
      size: 10000,
      filters: [
        {
          filterField: 'level',
          filterValue: level.toString(),
          filterOperator: 0
        }
      ]
    }).pipe(
      map(response => {
        const items = this.extractItems(response);
        return items.map(item => ({
          id: item.id,
          name: item.names?.['ru-RU'] || item.names?.['ru'] || item.names?.['en-US'] || item.name || '—',
          parentId: item.parentId ?? null,
          level: item.level,
          regionMapId: item.regionMapId ?? null,
          names: item.names
        }) as RegionDictionaryNode);
      })
    );
  }

  private extractItems(response: RegionQueryResponse): any[] {
    if (!response) {
      return [];
    }

    if (Array.isArray(response)) {
      return response;
    }

    if (response.items && Array.isArray(response.items)) {
      return response.items;
    }

    if (Array.isArray(response.data)) {
      return response.data;
    }

    if (response.data && Array.isArray((response.data as any).items)) {
      return (response.data as any).items;
    }

    return [];
  }

  private postQuery(url: string, payload: any): Observable<any> {
    const token = this.authService.getToken();
    const headers: HttpHeaders = token
      ? new HttpHeaders({
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        })
      : new HttpHeaders({
          'Content-Type': 'application/json'
        });

    return this.http.post(url, payload, { headers });
  }
}

